import { useSelector } from 'react-redux';

import Button from 'src/components/ui/Button';
import { selectGame } from 'src/slices/game.slice';

const DEFAULT_ASSETS_DOWNLOAD_BASENAME = 'assets';
const ASSETS_ZIP_EXTENSION = '.zip';
const ASSETS_ZIP_MIME_TYPE = 'application/zip';
const SAVE_PICKER_CANCELLED = Symbol('save-picker-cancelled');

function createAssetsRequestId() {
    return window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function sanitizeFilenameBaseName(filename) {
    const sanitizedFilename = filename
        .trim()
        .replace(/[<>:"/\\|?*]+/g, '-')
        .replace(/\s+/g, ' ')
        .replace(/\.+$/g, '');

    return sanitizedFilename || DEFAULT_ASSETS_DOWNLOAD_BASENAME;
}

function getAssetsDownloadFilename({ gameTitle, originalFilename }) {
    const originalFilenameWithoutExtension = originalFilename?.replace(/\.[^/.]+$/, '');
    const filenameBaseName = sanitizeFilenameBaseName(gameTitle || originalFilenameWithoutExtension || DEFAULT_ASSETS_DOWNLOAD_BASENAME);

    return `${filenameBaseName}${ASSETS_ZIP_EXTENSION}`;
}

async function getAssetsSaveFileHandle(downloadFilename) {
    if (typeof window.showSaveFilePicker !== 'function') {
        return null;
    }

    try {
        return await window.showSaveFilePicker({
            suggestedName: downloadFilename,
            types: [
                {
                    description: 'ZIP archive',
                    accept: {
                        [ASSETS_ZIP_MIME_TYPE]: ['.zip'],
                    },
                },
            ],
        });
    } catch (error) {
        if (error.name === 'AbortError') {
            return SAVE_PICKER_CANCELLED;
        }

        console.warn('Could not open assets save picker; using browser download instead:', error);
        return null;
    }
}

function downloadAssetsBlob(blob, downloadFilename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = url;
    a.download = downloadFilename;
    a.click();

    setTimeout(() => URL.revokeObjectURL(url));
}

async function saveAssetsBlob(blob, saveFileHandle, downloadFilename) {
    if (!saveFileHandle) {
        downloadAssetsBlob(blob, downloadFilename);
        return;
    }

    const writable = await saveFileHandle.createWritable();
    await writable.write(blob);
    await writable.close();
}

export default function AssetsDownload() {
    const { gameTitle, originalFilename } = useSelector(selectGame);
    const downloadFilename = getAssetsDownloadFilename({ gameTitle, originalFilename });

    const handleClickDownload = async () => {
        const iframe = document.querySelector('iframe');
        const targetWindow = iframe?.contentWindow;

        if (!targetWindow?.postMessage) {
            return;
        }

        const saveFileHandle = await getAssetsSaveFileHandle(downloadFilename);
        if (saveFileHandle === SAVE_PICKER_CANCELLED) {
            return;
        }

        const requestId = createAssetsRequestId();

        const onMessage = (event) => {
            if (event.source !== targetWindow || event.data?.requestId !== requestId) {
                return;
            }

            if (event.data?.type === 'pokiAssetsError') {
                console.error('Error received from iframe:', event.data);
                window.removeEventListener('message', onMessage);
                return;
            }

            if (event.data?.type !== 'pokiAssets') {
                return;
            }

            const { blob } = event.data;
            window.removeEventListener('message', onMessage);

            if (!(blob instanceof Blob)) {
                console.error('Assets response did not include a zip blob:', event.data);
                return;
            }

            saveAssetsBlob(blob, saveFileHandle, downloadFilename).catch(error => {
                console.error('Failed to save assets zip:', error);
            });
        };

        window.addEventListener('message', onMessage);

        targetWindow.postMessage({ type: 'pokiGetAssets', requestId }, '*');
    };

    return (
        <Button onClick={handleClickDownload}>
            Download Assets
        </Button>
    );
}
